import { STATIC_PROPOSALS, getStaticProposalBySlug } from '../data/staticProposals';
import { mergeProgramMediaKitLibrary } from '../utils/programMediaKitLibrary';

function resolveLandingApiBaseUrl() {
  const fromEnv = (process.env.REACT_APP_LANDING_API_URL || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return process.env.NODE_ENV === 'production' ? 'https://ads.tnonetwork.com' : 'http://localhost:3004';
}

const API_BASE_URL = resolveLandingApiBaseUrl();

class LandingApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  /**
   * Lista de propuestas: del API + estáticas que no vengan del API.
   * Si el API falla, se devuelven solo las estáticas.
   */
  async getProposals() {
    let fromApi = [];
    try {
      const url = `${this.baseUrl}/api/landing/proposals/public`;
      if (process.env.NODE_ENV === 'development') console.log('📄 Media Kit: cargando propuestas desde', url);
      const response = await fetch(url);
      if (response.ok) fromApi = await response.json();
    } catch (error) {
      console.error('Error fetching proposals from API:', error);
    }
    const bySlug = new Map();

    (fromApi || []).forEach((apiP) => {
      bySlug.set(apiP.slug, { ...apiP, _fromStatic: false });
    });

    STATIC_PROPOSALS.forEach((staticP) => {
      const existing = bySlug.get(staticP.slug);
      if (!existing) {
        bySlug.set(staticP.slug, { ...staticP, _fromStatic: true });
        return;
      }
      if (staticP.type === 'client') {
        bySlug.set(staticP.slug, { ...staticP, ...existing, type: 'client', _fromStatic: true });
        return;
      }
      if (staticP.type === 'program' && existing.type !== 'program') {
        bySlug.set(staticP.slug, { ...staticP, ...existing, type: 'program', _fromStatic: true });
        return;
      }
      if (staticP.type === 'program' && existing.type === 'program') {
        bySlug.set(staticP.slug, { ...staticP, ...existing, type: 'program', _fromStatic: false });
      }
    });

    const combined = Array.from(bySlug.values());
    combined.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    return combined;
  }

  /** Biblioteca completa de media kits de programa (estáticos + API + grilla). */
  async getProgramMediaKits() {
    const proposals = await this.getProposals();
    return mergeProgramMediaKitLibrary(proposals);
  }

  /**
   * Solo propuesta estática por slug (sin llamar al API). Para rutas directas /slug.
   * @returns {Promise<object|null>}
   */
  getProposalBySlugStatic(slug) {
    const p = getStaticProposalBySlug(slug);
    return Promise.resolve(p || null);
  }

  /**
   * Propuesta por slug: estática primero (landings dedicadas); si no hay, intenta API.
   */
  async getProposalBySlug(slug) {
    const staticFirst = getStaticProposalBySlug(slug);
    if (staticFirst) {
      return staticFirst;
    }
    try {
      const url = `${this.baseUrl}/api/landing/proposals/public/${encodeURIComponent(slug)}`;
      if (process.env.NODE_ENV === 'development') console.log('📄 Media Kit: cargando propuesta desde', url);
      const response = await fetch(url);
      if (response.ok) return response.json();
      throw new Error(`HTTP error! status: ${response.status}`);
    } catch (error) {
      console.error('Error fetching proposal by slug:', error);
      throw error;
    }
  }

  async getPublicLandingData() {
    try {
      console.log('🔍 Fetching landing data from:', `${this.baseUrl}/api/landing/public`);
      const response = await fetch(`${this.baseUrl}/api/landing/public`);
      console.log('📡 Response status:', response.status);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      console.log('✅ Data received:', data);
      console.log('💰 Pricing options from API:', data.pricingOptions?.length || 0);
      return data;
    } catch (error) {
      console.error('❌ Error fetching landing data:', error);
      console.log('🔄 Using default data as fallback');
      // Retornar datos por defecto en caso de error
      const defaultData = this.getDefaultData();
      console.log('💰 Pricing options from default:', defaultData.pricingOptions?.length || 0);
      return defaultData;
    }
  }

  getDefaultData() {
    return {
      content: [
        {
          section: 'who',
          title: 'TNO Network: El ecosistema de contenidos que amplifica tu marca',
          subtitle: 'Desde 2014, pioneros en la convergencia de streaming, cultura podcast y periodismo digital. Conectamos marcas con audiencias activas que aprenden, disfrutan y evolucionan a través de múltiples formatos.',
          description: 'Somos una red multimedia diseñada para que las empresas proyecten su identidad con impacto visual y autoridad editorial. Al integrar TNO Media Lab, nuestro centro de producción avanzada, y TNO Digest, nuestra plataforma editorial, redefinimos la publicidad convencional. Creamos narrativas interactivas, visuales y escritas que generan una conexión real y multicanal con tu audiencia.',
        },
        {
          section: 'why',
          title: '¿Por qué TNO Network?',
          description: 'Somos una red multimedia diseñada para que las empresas proyecten su identidad con impacto visual y autoridad editorial. Al integrar TNO Media Lab, nuestro centro de producción avanzada, y TNO Digest, nuestra plataforma editorial, redefinimos la publicidad convencional. Creamos narrativas interactivas, visuales y escritas que generan una conexión real y multicanal con tu audiencia.',
        },
        {
          section: 'benefits',
          title: 'Lo que nos hace únicos',
          description: 'Somos una red audiovisual y editorial con más de una década de trayectoria. Combinamos la fuerza del Live Streaming global con la permanencia del contenido On-Demand y la autoridad del periodismo digital a través de www.tnonetwork.com.',
        },
        {
          section: 'values',
          title: 'Nuestros Principios',
          description: 'Creemos en la conexión como el motor de experiencias inolvidables. Transformamos cada interacción en una oportunidad para explorar e innovar. Nuestra comunidad es el espacio donde la tecnología y la pasión se encuentran para redefinir el consumo de medios desde cualquier rincón del mundo.',
        },
        {
          section: 'audience',
          title: 'Nuestra Audiencia',
          description: 'Profesionales universitarios, bilingües y tomadores de decisiones. Personas con un estilo de vida activo que valoran la practicidad y se informan exclusivamente a través de medios digitales. Son consumidores exigentes que buscan contenido que les aporte valor personal y crecimiento profesional.',
        },
        {
          section: 'buyer-persona',
          title: 'The Modern Decision Maker',
          description: 'Profesionales universitarios, bilingües y tomadores de decisiones. Personas con un estilo de vida activo que valoran la practicidad y se informan exclusivamente a través de medios digitales.',
        },
        {
          section: 'global-community-map',
          title: 'Comunidad Global',
          description: 'Consolidamos tu identidad de marca ante una audiencia internacional mediante desarrollos digitales innovadores.',
        },
        {
          section: 'metrics',
          title: 'Nuestros Números',
          description: 'Consolidamos tu identidad de marca ante una audiencia internacional mediante desarrollos digitales innovadores.',
        },
        {
          section: 'pricing',
          title: 'Opciones de Comercialización',
          description: 'Anuncia con nosotros y alcanza tu audiencia ideal.',
        },
        {
          section: 'are-you-ready',
          title: '¿Estás listo para dejar atrás lo convencional?',
          description: 'Únete a TNO Network y desata el poder de la innovación multimedia. Alcanza a tu audiencia, escala tu impacto y haz que tu marca resuene en cada rincón del mundo digital.',
        },
      ],
      metrics: [
        {
          title: 30000,
          label: 'Visitas mensuales en plataforma web y editorial',
          subtitle: 'Alcance constante y crecimiento sostenido.',
          suffix: '+',
          icon: 'LanguageIcon',
        },
        {
          title: 400000,
          label: 'Cuentas alcanzadas en Instagram',
          subtitle: 'Visualizaciones de alcance en la cuenta de TNO Network al cierre de mayo.',
          suffix: '+',
          icon: 'InstagramIcon',
        },
        {
          title: 90000,
          label: 'Seguidores totales en ecosistema de redes sociales',
          subtitle: 'Instagram, X, YouTube combinados.',
          suffix: '+',
          icon: 'InstagramIcon',
        },
        {
          title: 40,
          label: 'Producciones originales On-Demand',
          subtitle: 'Desarrolladas en el Media Lab.',
          suffix: '+',
          icon: 'OndemandVideoIcon',
        },
        {
          title: 25,
          label: 'Programas en vivo semanalmente',
          subtitle: 'Contenido fresco y constante.',
          suffix: '+',
          icon: 'LiveTvIcon',
        },
      ],
      pricingOptions: [
        {
          title: 'Plan Brand Awareness',
          annualPrice: 400,
          monthlyPrice: 150,
          features: [
            'Rotación de Spot Visual (20 seg) - 6 veces al día',
            'Presencia en Bloque Comercial (No invasivo)',
            'Logo rotativo en cintillo digital durante programación',
            'Reporte básico de impactos mensuales',
          ],
          idealFor: 'Lanzamientos de producto, Retail, Farmacias y Bodegones que requieren repetición constante.',
          duration: '1 Mes',
          buttonText: 'Iniciar Campaña',
          highlightedTitle: 'Tu marca, todo el día',
          highlightedContent: [
            'La repetición es la clave del posicionamiento.',
            'Garantizamos que tu marca sea vista en los horarios pico de nuestra transmisión global.',
            'Más efectivo y económico que una valla estática.',
          ],
          highlightedButtonText: 'Ver Muestra al Aire',
        },
        {
          title: 'Plan Show Partner',
          annualPrice: 950,
          monthlyPrice: 350,
          features: [
            'Patrocinante Oficial de 1 Programa Prime',
            'Menciones en vivo por el Host (Product Placement)',
            'Logo fijo en pantalla durante el show (Corner)',
            '1 Entrevista mensual de valor (15 min)',
          ],
          idealFor: 'Marcas de Salud, Tecnología, Seguros y Servicios que necesitan explicación y confianza.',
          duration: '3 Meses (Trimestral)',
          buttonText: 'Ser Patrocinante',
          highlightedTitle: 'Humaniza tu Venta',
          highlightedContent: [
            'No interrumpas el contenido, sé parte de él.',
            'Aprovecha la fidelidad de la audiencia de nuestros talentos.',
            'Tu producto en la mesa de los líderes de opinión, no en un corte comercial.',
          ],
          highlightedButtonText: 'Consultar Programas',
        },
        {
          title: 'Plan TNO Ecosystem',
          annualPrice: 1600,
          monthlyPrice: 600,
          features: [
            'Presencia Cross-Media (Spot en Vivo + Banner Web)',
            'Publirreportaje mensual en TNO Digest (SEO)',
            'Campaña de Redes Sociales (Collabs en IG/TikTok)',
            'Producción de Branded Content (Micro exclusivo)',
          ],
          idealFor: 'Banca, Automotriz, Telecomunicaciones y Marcas que buscan autoridad digital completa.',
          duration: 'Trimestral / Semestral',
          buttonText: 'Hablar con Ventas',
          highlightedTitle: 'Omnicanalidad Real',
          highlightedContent: [
            'Ataca todos los frentes: Video, Lectura y Viralidad.',
            'Mejora tu SEO apareciendo en nuestro portal de noticias.',
            'Convertimos tu publicidad en contenido que la gente quiere ver y compartir.',
          ],
          highlightedButtonText: 'Agendar Reunión',
          icon: 'HubIcon',
        },
        {
          title: 'Banner Ads Network',
          annualPrice: 210,
          monthlyPrice: 80,
          features: [
            'Banner Principal (Hero) en portal TNO Digest',
            'Ubicación estratégica en artículos de tendencia',
            'Enlace directo (Backlink) a tu web o WhatsApp',
            'Impresiones ilimitadas durante el periodo',
          ],
          idealFor: 'E-commerce, landing pages de servicios y marcas que buscan tráfico web cualificado.',
          duration: '1 Mes',
          buttonText: 'Activar Banners',
          highlightedTitle: 'Tráfico Residual',
          highlightedContent: [
            'Aprovecha el flujo de lectores de nuestro CMS.',
            'Mejora tu presencia en buscadores con tráfico desde un dominio de autoridad.',
            'Tu marca presente mientras el usuario consume información relevante.',
          ],
          highlightedButtonText: 'Ver Ubicaciones',
        },
        {
          title: 'Social Insert Edition',
          annualPrice: 310,
          monthlyPrice: 120,
          features: [
            'Inserción de logo/marca en 8 clips mensuales',
            'Pauta en Reels, TikTok y YouTube Shorts',
            'Uso de marca en "Momentos de Oro" virales',
            'Etiqueta de marca en la descripción del post',
          ],
          idealFor: 'Marcas de consumo masivo, ropa, apps y negocios que buscan el público joven y viral.',
          duration: '1 Mes',
          buttonText: 'Elegir Pack Clips',
          highlightedTitle: 'Efecto Viral',
          highlightedContent: [
            'Tu marca viaja con nuestro contenido más compartido.',
            'Impacta a audiencias que no ven radio en vivo pero sí consumen redes sociales.',
            'Presencia garantizada en los fragmentos con mayor "engagement" de la semana.',
          ],
          highlightedButtonText: 'Ver Ejemplo de Clip',
        },
        {
          title: 'Ghost Live Branding',
          annualPrice: 550,
          monthlyPrice: 200,
          features: [
            'Logo fijo o animado en pantalla (Corner/Cintillo)',
            'Product Placement en mesa (Sin mención verbal)',
            'Uso de marca en pantallas LED de fondo',
            'Presencia en cortinillas de entrada/salida',
          ],
          idealFor: 'Marcas premium o corporativas que buscan prestigio y asociación visual sin ser ruidosas.',
          duration: 'Trimestral',
          buttonText: 'Contratar Espacio',
          highlightedTitle: 'Prestigio Visual',
          highlightedContent: [
            'Sin interrupciones. Tu marca es parte de la escenografía digital.',
            'Presencia visual durante el 100% de la entrevista o programa.',
            'Ideal para generar familiaridad de marca de forma subconsciente y elegante.',
          ],
          highlightedButtonText: 'Ver Layout de Pantalla',
        },
      ],
    };
  }

  getContentBySection(section) {
    return this.getPublicLandingData().then(data => {
      return data.content.find(item => item.section === section);
    });
  }

  getMetrics() {
    return this.getPublicLandingData().then(data => {
      return data.metrics;
    });
  }

  getPricingOptions() {
    return this.getPublicLandingData().then(data => {
      return data.pricingOptions;
    });
  }
}

const landingApiService = new LandingApiService();
export default landingApiService; 